import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Callout from "components/callout/callout";
import Section from "components/layout/section";
import Doc from "components/list/docList/doc";
import IconButton from "../buttons/iconButton";
import { ReactComponent as CheckIcon } from 'assets/icons/check.svg';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import { ReactComponent as DeleteIcon } from 'assets/icons/delete.svg';
import { ReactComponent as LinkIcon } from 'assets/icons/link.svg';
import useNotifications from "@/hooks/useNotifications";
import useLayout from "@/hooks/useLayout";
import { setTargetStorage } from "@/utils/targetStorage";
import { FormStatus, Notification } from "@/types";
import DateTime from "@/components/text/DateTime";

const productLinkIds = (notification: Notification) => {
    const storageDocId = notification.storageDocId || notification.ids?.storageId;
    const itemDocId = notification.itemDocId || notification.ids?.productId;
    if (!storageDocId || !itemDocId) return null;
    return { storageDocId, itemDocId };
};

const NotificationList: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { openDialog, addToast } = useLayout();

    const {
        notifications,
        markAsRead,
        markAsUnread,
        notificationsStatus
    } = useNotifications();

    const openProduct = (storageDocId: string, itemDocId: string) => {
        setTargetStorage(storageDocId, '');
        navigate(`/storage/products/${itemDocId}`);
    };

    const updateReadState = async (notificationId: string | undefined, isRead: boolean) => {
        if (!notificationId) return;
        const result = isRead
            ? await markAsRead(notificationId)
            : await markAsUnread(notificationId);
        if (result.status === FormStatus.ERROR) {
            addToast({ text: t('toastNotificationUpdateError'), colorScheme: 'red' });
        }
    };

    const openNotification = (notification: Notification) => {
        if (!notification.id) return;
        openDialog('view_notification', { notificationId: notification.id });
        if (!notification.isRead) {
            void updateReadState(notification.id, true);
        }
    };

    if (notificationsStatus === FormStatus.ERROR) {
        return <Callout type="error">{t('errorLoadingData')}</Callout>;
    }

    if (notificationsStatus === FormStatus.SUCCESS && notifications.length === 0) {
        return (
            <Section>
                <Callout type="warning">
                    {t('calloutNoNotifications')}
                </Callout>
            </Section>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            {notificationsStatus === FormStatus.LOADING &&
                Array.from({ length: 3 }).map((_, index) => (
                    <Doc key={index} isLoading={true}>
                        <p className="skeleton"></p>
                        <p className="skeleton"></p>
                    </Doc>
                ))
            }
            {notifications.map((notification) => {
                const link = productLinkIds(notification);
                return (
                    <Doc
                        key={notification.id}
                        title={notification.title}
                        className={`!flex-row items-center gap-4 ${notification.isRead ? '' : '!bg-yellow-100'}`}
                        onClick={() => openNotification(notification)}
                        rightColumn={
                            <div className="flex items-center gap-2" onClick={(event) => event.stopPropagation()}>
                                {!notification.isRead && (
                                    <IconButton
                                        icon={<CheckIcon />}
                                        size="small"
                                        colorScheme="green"
                                        onClick={() => updateReadState(notification.id, true)}
                                    />
                                )}
                                {notification.isRead && (
                                    <IconButton
                                        icon={<CloseIcon />}
                                        size="small"
                                        colorScheme="yellow"
                                        onClick={() => updateReadState(notification.id, false)}
                                    />
                                )}
                                {link && (
                                    <IconButton
                                        icon={<LinkIcon />}
                                        size="small"
                                        colorScheme="blue"
                                        onClick={() => openProduct(link.storageDocId, link.itemDocId)}
                                    />
                                )}
                                <IconButton
                                    icon={<DeleteIcon />}
                                    size="small"
                                    colorScheme="red"
                                    onClick={() => openDialog('delete_notification', { notificationId: notification.id })}
                                />
                            </div>
                        }
                    >
                        <DateTime timestamp={notification.createdAt} className="text-sm text-gray-500" />
                        {notification.message}
                    </Doc>
                );
            })}
        </div>
    );
};

export default NotificationList;
